/**
 * Library lesson "highlights as data" — pure glue between the shared text-quote
 * anchor engine (utils/highlightAnchor.js) and the lesson viewer
 * (pages/library/lesson_view.vue). See docs/lesson-highlights-rewrite.md.
 *
 * The lesson page renders each section as a v-html island keyed by
 * random_section_id. Legacy highlights are ONE rangy blob per (user, lesson)
 * with raw character offsets scoped by that section id
 * (`type:textContent|start$end$id$class$sectionId|...`). The new model stores a
 * parallel anchors envelope `{"v":1,"anchors":[...]}` in
 * library_user_highlights.highlights_anchors, where every anchor is a W3C-style
 * text-quote anchor PLUS `container` = the section id, and rendering bakes
 * <mark> into the section's HTML STRING (decorateSection) — Vue owns the marks,
 * zero live-DOM surgery.
 *
 * Everything here is pure (string/data in -> data out) except
 * resolveSectionElFromNode (a DOM walk-up used by the create path) and
 * resetHighlighterState (a rangy state reset used by the legacy-path bug fix).
 *
 * Lessons carry NO anti-piracy watermark, so highlightAnchor's watermark
 * stripping is a verified no-op on lesson HTML (the exact-shape regex can never
 * match a real lesson link).
 *
 * Safari 12 / iOS 12: standalone .js — NOT reliably transpiled. No optional
 * chaining (?.), no nullish coalescing (??), no ES2020+.
 */
/* eslint-env commonjs */

var anchorLib = require('./highlightAnchor');
var containment = require('./highlightContainment');

var BLOB_HEADER = 'type:textContent';

// Legacy blob classes the lesson highlighter ever wrote (the 5 color appliers
// registered in beforeMountFunction). Anything else (defensive) stays blob-only.
var LESSON_MARKER_CLASSES = {
  'marker-yellow': true,
  'marker-green': true,
  'marker-blue': true,
  'marker-pink': true,
  'marker-orange': true,
};

// Soft cap on the dual-written legacy blob. library_user_highlights.
// highlights_data is TEXT (64KB) and MySQL strict mode REJECTS an oversized
// write — today that failure is swallowed server-side and the whole save is
// silently lost. Below this cap we keep dual-writing; above it, new highlights
// are anchor-only (old bundles miss only the overflow tail).
var BLOB_SOFT_CAP = 60000;

/**
 * Kill-switch / rollout flag for the new lesson highlight renderer.
 * Now the DEFAULT for everyone (the "highlights as data" renderer bakes <mark>
 * into the island HTML instead of live rangy DOM surgery). Disabled only when
 * localStorage 'lesson_highlight_renderer' === '0' — a permanent per-browser
 * kill switch: set it to '0' and reload to fall back to the legacy renderer.
 */
function isEnabled() {
  try {
    return window.localStorage.getItem('lesson_highlight_renderer') !== '0';
  } catch (e) {
    return true;
  }
}

// Phase B annotation renderer. Now the DEFAULT (baked annotation spans instead
// of live rangy DOM surgery). Kill switch: localStorage
// 'lesson_annotation_renderer' = '0'. REQUIRES the Phase A highlight renderer —
// with A off, highlights are live rangy DOM spans and any annotation re-bake
// would wipe them, so annotations-as-data only runs on top of the decorated
// pipeline.
var ANNOTATION_DATA_DEFAULT_ON = true;

function isAnnotationEnabled() {
  try {
    if (!isEnabled()) return false;
    var v = window.localStorage.getItem('lesson_annotation_renderer');
    if (v === '0') return false;
    if (v === '1') return true;
    return ANNOTATION_DATA_DEFAULT_ON;
  } catch (e) {
    return false;
  }
}

/**
 * Parse the stored anchors column value. Returns the anchors array (each entry
 * a { quote, prefix, suffix, start, end, occ, docLen, color, id, container }
 * object), or null when the value is empty/malformed/not-an-envelope. Entries
 * without a non-empty string quote or container are dropped (they could never
 * render and must not poison the set).
 */
function parseAnchorColumn(raw) {
  if (typeof raw !== 'string') return null;
  var t = raw.replace(/^\s+|\s+$/g, '');
  if (t === '' || t === 'null' || t === 'undefined' || t === '[]') return null;
  var parsed;
  try {
    parsed = JSON.parse(t);
    // eslint-disable-next-line
  } catch (e) {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || !parsed.anchors || !parsed.anchors.length) {
    return null;
  }
  var out = [];
  for (var i = 0; i < parsed.anchors.length; i++) {
    var a = parsed.anchors[i];
    if (a && typeof a.quote === 'string' && a.quote !== '' &&
        typeof a.container === 'string' && a.container !== '') {
      out.push(a);
    }
  }
  return out.length ? out : null;
}

/** Serialize anchors to the storage envelope; '' for an empty/absent set. */
function serializeAnchors(anchors) {
  if (!anchors || !anchors.length) return '';
  return JSON.stringify({ v: 1, anchors: anchors });
}

/**
 * Append a legacy `start$end$id$class$container` entry to a rangy blob,
 * creating the header when the blob is empty or not a rangy serialization.
 * A malformed entry (containing '|') is refused — blob returned unchanged.
 */
function appendEntryToBlob(blob, entryStr) {
  var base = typeof blob === 'string' ? blob : '';
  if (!entryStr || typeof entryStr !== 'string' || entryStr.indexOf('|') !== -1) {
    return base;
  }
  if (base.indexOf(BLOB_HEADER) === 0) {
    return base + '|' + entryStr;
  }
  return BLOB_HEADER + '|' + entryStr;
}

/**
 * Remove the blob entries whose id is in `ids` (array or {id:true} map).
 * Non-rangy blobs are returned untouched. When no entries survive, returns ''
 * — the library backend stores '' WITHOUT deleting the row (lesson lifecycle;
 * deliberately different from the question side's empty=>DELETE).
 */
function removeEntriesFromBlob(blob, ids) {
  var entries = containment.parseBlob(blob);
  if (entries === null) return typeof blob === 'string' ? blob : '';
  var idMap = {};
  if (ids && typeof ids === 'object' && !Array.isArray(ids)) {
    idMap = ids;
  } else if (Array.isArray(ids)) {
    for (var i = 0; i < ids.length; i++) idMap[String(ids[i])] = true;
  }
  var survivors = [];
  for (var j = 0; j < entries.length; j++) {
    if (!idMap[String(entries[j].id)]) survivors.push(entries[j].raw);
  }
  if (!survivors.length) return '';
  return BLOB_HEADER + '|' + survivors.join('|');
}

/**
 * MIGRATION: derive text-quote anchors from the legacy rangy blob, entirely
 * from the pristine section HTML strings (parseInertBody — raw textContent of
 * parsed HTML equals the live island's textContent, so this is DOM-timing-free
 * and never touches the page).
 *
 * - `sections`: the lesson's sections (objects carrying random_section_id +
 *   section_content, i.e. sections_in_concept entries).
 * - `blob`: the stored highlights_data value.
 * - `seenIds`: ids already present as first-class anchors ({id:true} map or
 *   array) — those entries are skipped (self-limiting persist-once).
 *
 * Entries that cannot be converted are counted in `skipped` and stay blob-only
 * FOREVER (preserved, never dropped): unknown/foreign section ids (cross-lesson
 * bleed artifacts — Invariant I9), non-marker classes, unrecoverable offsets.
 */
function migrateLegacyBlob(sections, blob, seenIds) {
  var out = { anchors: [], skipped: 0 };
  var entries = containment.parseBlob(blob);
  if (entries === null || !entries.length) return out;

  var seen = {};
  if (seenIds && typeof seenIds === 'object' && !Array.isArray(seenIds)) {
    seen = seenIds;
  } else if (Array.isArray(seenIds)) {
    for (var s = 0; s < seenIds.length; s++) seen[String(seenIds[s])] = true;
  }

  var sectionById = {};
  var list = sections || [];
  for (var i = 0; i < list.length; i++) {
    var sec = list[i];
    if (!sec) continue;
    var sid = sec.random_section_id !== undefined && sec.random_section_id !== null
      ? String(sec.random_section_id)
      : (sec.id !== undefined && sec.id !== null ? String(sec.id) : '');
    if (sid) sectionById[sid] = sec;
  }

  var bodyCache = {};
  for (var e = 0; e < entries.length; e++) {
    var entry = entries[e];
    if (entry.id !== undefined && seen[String(entry.id)]) continue; // already migrated
    if (!LESSON_MARKER_CLASSES[entry.className]) { out.skipped++; continue; }
    var cid = entry.containerId ? String(entry.containerId) : '';
    var section = cid ? sectionById[cid] : null;
    if (!section) { out.skipped++; continue; } // foreign/unknown section: blob-only
    var body = bodyCache[cid];
    if (body === undefined) {
      body = anchorLib.parseInertBody(
        section.section_content !== undefined ? section.section_content : ''
      );
      bodyCache[cid] = body;
    }
    if (!body) { out.skipped++; continue; }
    var anchor = anchorLib.anchorFromOldEntry(body, entry.start, entry.end, entry.className, entry.id);
    if (!anchor) { out.skipped++; continue; }
    anchor.container = cid;
    out.anchors.push(anchor);
  }
  return out;
}

/**
 * RE-HOMING: for every anchor whose container section is gone (section re-ID
 * in the admin Builder / AI replace_all) or no longer matches its own section,
 * try match() against every OTHER section's clean text. EXACTLY ONE hit ->
 * adopt that section id; zero or ambiguous -> stay orphan (preserved,
 * unrendered — never guess). Returns { anchorId: newContainerId }.
 * `cleanCache` ({sectionId: cleanText}) is filled lazily and can be shared.
 */
function computeRehomes(sections, anchors, cleanCache) {
  var out = {};
  if (!anchors || !anchors.length) return out;
  var cache = cleanCache && typeof cleanCache === 'object' ? cleanCache : {};
  var list = sections || [];

  function cleanOf(sec, sid) {
    if (cache[sid] === undefined) {
      cache[sid] = anchorLib.cleanTextFromHtml(
        sec.section_content !== undefined ? sec.section_content : ''
      );
    }
    return cache[sid];
  }

  var idOf = function (sec) {
    if (!sec) return '';
    if (sec.random_section_id !== undefined && sec.random_section_id !== null) {
      return String(sec.random_section_id);
    }
    return sec.id !== undefined && sec.id !== null ? String(sec.id) : '';
  };

  var sectionIds = {};
  for (var i = 0; i < list.length; i++) {
    var sid0 = idOf(list[i]);
    if (sid0) sectionIds[sid0] = list[i];
  }

  for (var a = 0; a < anchors.length; a++) {
    var anchor = anchors[a];
    if (!anchor || !anchor.id) continue;
    var own = anchor.container ? String(anchor.container) : '';
    if (own && sectionIds[own] &&
        anchorLib.match(cleanOf(sectionIds[own], own), anchor) !== null) {
      continue; // still resolves at home — nothing to do
    }
    var hits = [];
    for (var k = 0; k < list.length; k++) {
      var sid = idOf(list[k]);
      if (!sid || sid === own) continue;
      if (anchorLib.match(cleanOf(list[k], sid), anchor) !== null) {
        hits.push(sid);
        if (hits.length > 1) break; // ambiguous — stop early
      }
    }
    if (hits.length === 1) out[String(anchor.id)] = hits[0];
  }
  return out;
}

/**
 * RENDER: bake this section's highlights into its HTML string. Pure
 * f(content, anchors) -> content'. Byte-identical passthrough when the section
 * has no renderable anchors (so v-html bindings never churn on unrelated
 * sections and Vue never touches their DOM). Orphans render nothing.
 */
function decorateSection(html, sectionId, anchors) {
  var raw = html === null || html === undefined ? '' : String(html);
  if (!anchors || !anchors.length || !sectionId) return raw;
  var mine = [];
  for (var i = 0; i < anchors.length; i++) {
    var a = anchors[i];
    if (a && String(a.container) === String(sectionId)) mine.push(a);
  }
  if (!mine.length) return raw;
  var ranges = anchorLib.resolveAnchorsToRanges(raw, mine);
  if (!ranges.length) return raw;
  return anchorLib.injectMarks(raw, ranges);
}

/**
 * ANNOTATION MIGRATION: derive a text-quote anchor for a legacy annotation row,
 * DOM-independently. Two paths, both quote-verified so the wrong-note class is
 * impossible:
 *   1. offsets-first — parse the legacy `serialized` rangy entry, convert via
 *      anchorFromOldEntry against the container section's inert body, and ACCEPT
 *      only if the derived quote equals normalize(selectedText) (or selectedText
 *      is empty);
 *   2. fallback — normalize(selectedText) must occur EXACTLY ONCE across ALL
 *      sections' clean text; otherwise return null (never guess a duplicate).
 * Returns an anchor object { quote,prefix,suffix,start,end,occ,docLen,container }
 * (no color/id — the row key is annotation_id), or null.
 */
function deriveAnnotationAnchor(sections, serialized, selectedText) {
  var list = sections || [];
  var sectionById = {};
  var i;
  for (i = 0; i < list.length; i++) {
    var sec = list[i];
    if (!sec) continue;
    var sid = sec.random_section_id !== undefined && sec.random_section_id !== null
      ? String(sec.random_section_id)
      : (sec.id !== undefined && sec.id !== null ? String(sec.id) : '');
    if (sid) sectionById[sid] = sec;
  }
  var normSel = anchorLib.normalize(selectedText === undefined || selectedText === null ? '' : selectedText);

  // Path 1: offsets, quote-verified.
  var entries = containment.parseBlob(serialized);
  if (entries && entries.length) {
    var e = entries[0];
    var section = e.containerId ? sectionById[e.containerId] : null;
    if (section) {
      var body = anchorLib.parseInertBody(
        section.section_content !== undefined ? section.section_content : ''
      );
      if (body) {
        var anchor = anchorLib.anchorFromOldEntry(body, e.start, e.end, '', '');
        if (anchor && (normSel === '' || anchor.quote === normSel)) {
          anchor.container = String(e.containerId);
          if (anchor.color !== undefined) delete anchor.color;
          if (anchor.id !== undefined) delete anchor.id;
          return anchor;
        }
      }
    }
  }

  // Path 2: unique selected_text across all sections.
  if (normSel !== '') {
    var total = 0;
    var hitSid = null;
    var hitStart = -1;
    var hitClean = null;
    for (i = 0; i < list.length; i++) {
      var s2 = list[i];
      if (!s2) continue;
      var sid2 = s2.random_section_id !== undefined && s2.random_section_id !== null
        ? String(s2.random_section_id)
        : (s2.id !== undefined && s2.id !== null ? String(s2.id) : '');
      if (!sid2) continue;
      var clean = anchorLib.cleanTextFromHtml(s2.section_content !== undefined ? s2.section_content : '');
      var occs = anchorLib.allIndicesOf(clean, normSel);
      if (occs.length) {
        if (total === 0 && occs.length === 1) {
          hitSid = sid2; hitStart = occs[0]; hitClean = clean;
        }
        total += occs.length;
        if (total > 1) break;
      }
    }
    if (total === 1 && hitSid !== null) {
      var a = anchorLib.describe(hitClean, hitStart, hitStart + normSel.length);
      if (a) { a.container = hitSid; return a; }
    }
  }
  return null;
}

/** New-format anchor id — cannot collide with legacy numeric rangy ids. */
function newAnchorId(seq) {
  return 'a' + Date.now().toString(36) + (seq !== undefined && seq !== null ? String(seq) : '');
}

/**
 * Walk up from a selection's node to the section island element (an ancestor
 * whose id is in `idSet` — {sectionId:true}). Returns the element or null.
 */
function resolveSectionElFromNode(node, idSet) {
  var n = node;
  var ids = idSet || {};
  while (n) {
    if (n.nodeType === 1 && n.id && ids[n.id]) return n;
    n = n.parentNode;
  }
  return null;
}

/**
 * BUG FIX (cross-lesson bleed): rangy's Highlighter keeps every deserialized/
 * created highlight in an internal array; it is never cleared on lesson
 * navigation, so lesson B's first serialize() used to emit lesson A's entries
 * into B's row. Truncating the array is a pure state reset with ZERO DOM
 * contact (removeAllHighlights() would do per-entry DOM surgery against nodes
 * the v-html swap already destroyed — the exact corruption class this project
 * removes). Called once per lesson load, flag-independent.
 */
function resetHighlighterState(h) {
  try {
    if (h && h.highlights && h.highlights.length) h.highlights.length = 0;
    // eslint-disable-next-line
  } catch (e) { /* never let a reset failure break the page */ }
}

module.exports = {
  BLOB_HEADER: BLOB_HEADER,
  BLOB_SOFT_CAP: BLOB_SOFT_CAP,
  LESSON_MARKER_CLASSES: LESSON_MARKER_CLASSES,
  isEnabled: isEnabled,
  isAnnotationEnabled: isAnnotationEnabled,
  deriveAnnotationAnchor: deriveAnnotationAnchor,
  parseAnchorColumn: parseAnchorColumn,
  serializeAnchors: serializeAnchors,
  appendEntryToBlob: appendEntryToBlob,
  removeEntriesFromBlob: removeEntriesFromBlob,
  migrateLegacyBlob: migrateLegacyBlob,
  computeRehomes: computeRehomes,
  decorateSection: decorateSection,
  newAnchorId: newAnchorId,
  resolveSectionElFromNode: resolveSectionElFromNode,
  resetHighlighterState: resetHighlighterState,
};
